import React from 'react';
import { positionMenuByTrigger } from '../../utils/menuPosition';
import {useSelector, useDispatch} from 'react-redux';
import {Button, Dropdown} from 'react-bootstrap';
import {
    setOrganizerFiles,
    setOrganizerProgress,
    setOrganizerIsProcessing,
    resetOrganizerFilePath,
    setOrganizerCancelRequested,
    setOrganizerAutoAnalyze
} from '../../redux/slices/organizerSlice';
import { useToast } from '../../contexts/ToastContext';
import { track } from '../../utils/analytics';
import { loadCloudPlaylistFiles } from '../../utils/cloudPlaylistTracks';
import { refreshAndRetry } from '../../redux/utils/refreshAndRetry';
import { confirmLargeSet, countAudioFiles, effectiveParallelism, SET_COST } from '../../utils/largeSetGuard';
import {clearLogs, updateOrAddLogs, removeLogsForFile} from '../../redux/slices/organizerLogsSlice';
import {formatPath} from "../../utils/formatPath";
import { statDigits } from "../../utils/statDigits";
import StatCount from "../StatCount";
import OrganizerSettings from "../OrganizerSettings";
import {
    setOperation, setMainOrganization, setSecondaryOrganization, setTertiaryOrganization,
    setIncludeMonth, setDateFormat, setAppendToBeginning
} from '../../redux/slices/organizerSettingsSlice';
import { useDesignV2 } from '../../utils/designV2';
import ToolHeader, { PopoverRow } from '../v2/ToolHeader';
import ToggleSwitch from '../ToggleSwitch';
import CustomSelect from '../CustomSelect';
import ProgressBarWithStop from "../ProgressBarWithStop.js";
import {ReactComponent as RefreshIcon} from '../../assets/icons/refresh.svg';
import {ReactComponent as DirectoryIcon} from '../../assets/icons/select-folder.svg';
import {ReactComponent as AudioFileIcon} from '../../assets/icons/audio-file.svg';
import {ReactComponent as AnalyzeFilesIcon} from '../../assets/icons/analyze-files.svg';
import {ReactComponent as EditFilenamesIcon} from '../../assets/icons/edit-filenames.svg';
import {ReactComponent as PlayIcon} from '../../assets/icons/play.svg';
import {ReactComponent as PauseIcon} from '../../assets/icons/pause.svg';
import {ReactComponent as StopIcon} from '../../assets/icons/stop.svg';
import {ReactComponent as OpenIcon} from '../../assets/icons/open.svg';
import {setAutoPlay, setAudioList, addTrack, resetAudioList} from '../../redux/slices/musicPlayerSlice';
import RowActions from '../RowActions';
import BulkRowContextMenu from '../BulkRowContextMenu';
import AddToPlaylistModal from '../AddToPlaylistModal';
import useRowSelection from '../../hooks/useRowSelection';
import CoverPlayButton from '../CoverPlayButton';
import { batchProgressTicks } from '../../utils/progressBatcher';
import { useEnrichedTracks, getEnrichedParts } from '../../hooks/useEnrichedTracks';
import { waitRemaining } from '../../utils/minVisibleDelay';
import { usePlayerRowSync } from '../../hooks/usePlayerRowSync';
import { setLocalListContext } from '../../utils/localListContext';
import { useTierGate } from '../../hooks/useTierGate';
import './SmartRecsTab.scss';

// ---- File row (module level, memoized) ----
// The preview trees are rebuilt from the logs on every tick, so node objects
// are always new — but a file row only depends on primitives (path, name,
// indent, selected/playing flags, its own context-menu anchor) plus the
// enrichment map and a stable `api` that reads the tab's latest handlers
// through a ref. Memoizing on those means a finished file re-renders its own
// row, not the whole tree (same approach as CompatLogRow / LoudnessLogRow).
const OrganizeFileRow = React.memo(function OrganizeFileRow({ filePath, name, indent, pane, selectable, selected, playing, ctxAt, enrichedMap, api }) {
    // Enriched (Mixpedia/ID3) identity beats the raw filename parse: the
    // menu shows/copies the real name, and a Like carries the catalog id.
    const enriched = filePath ? getEnrichedParts(enrichedMap, filePath) : null;
    return (
        <div
            className="tree-node"
            data-rowkey={selectable ? filePath : undefined}
            style={{
                marginLeft: `${indent}px`,
                ...(selected ? {
                    background: 'var(--row-selected-bg)',   // wash + painted bar (_theme.scss)
                    // selected rows TOUCH: square, no 1px gap (the blue-grey panel showed
                    // through it between grey rows), so a range is one block with one
                    // unbroken bar; the gap's 2px move into the padding — nothing shifts
                    borderRadius: 0,
                    marginTop: 0,
                    marginBottom: 0,
                    paddingTop: 6,   // top/bottom only: Organize indents rows with marginLeft
                    paddingBottom: 6,
                } : {}),
            }}
            onClick={selectable ? (e) => api.rowClick(filePath, e) : undefined}
            // Shift-click extends the selection — suppress the browser's
            // native shift-click text-selection so the two don't fight.
            onMouseDown={selectable ? (e) => { if (e.shiftKey) e.preventDefault(); } : undefined}
            onContextMenu={filePath ? (e) => api.rowContextMenu(filePath, e, pane) : undefined}
            onDoubleClick={(e) => api.playClick(filePath, e)}
        >
            {filePath && (
                <CoverPlayButton
                    filePath={filePath}
                    cover={enriched.cover}
                    isPlaying={playing}
                    onClick={(e) => playing ? api.pauseClick(e) : api.playClick(filePath, e)}
                />
            )}
            <span className="tree-node-text">{name}</span>
            {filePath && (
                <RowActions
                    track={{ title: enriched.title, artist: enriched.artist, filePath, artwork: enriched.cover || null, djpalId: enriched.djpalId || null }}
                    onAddToQueue={() => api.addToQueue(filePath, { stopPropagation() {} })}
                    onRemoveFromLibrary={() => api.removeFromList([filePath], 'menu')}
                    removeLabel="Remove from list"
                    openAtPoint={ctxAt}
                    desktopLike
                    analyticsFrom="organize_tracks"
                />
            )}
        </div>
    );
});

const OrganizeTracksTab = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { canAccess, requireUpgrade } = useTierGate('smart-organize');

    // Local state for playback control
    const [currentlyPlayingPath, setCurrentlyPlayingPath] = React.useState(null);
    const [isPlaying, setIsPlaying] = React.useState(false);
    // Reconcile the row play/pause icon when the user pauses/skips from the
    // bottom player (otherwise the row stays stuck showing "playing"), and
    // move the highlight to the next row on list auto-advance.
    usePlayerRowSync(currentlyPlayingPath, setIsPlaying, setCurrentlyPlayingPath);
    const [isDragging, setIsDragging] = React.useState(false);

    // Right-click menus: single-row opens the row's shared TrackContextMenu at
    // the cursor (identical to its ⋯ menu); a right-click inside a multi-row
    // selection opens the bulk menu. rowCtx keys are `${pane}:${filePath}` —
    // the same file renders in both tree panes.
    const [rowCtx, setRowCtx] = React.useState(null);   // { key, x, y, nonce }
    const [bulkCtx, setBulkCtx] = React.useState(null); // { x, y }
    const ctxNonceRef = React.useRef(0);
    // Bulk "Add N Tracks to Playlist" picker targets.
    const [bulkPlaylistTargets, setBulkPlaylistTargets] = React.useState([]);


    // Playlist dropdown state
    const [selectedPlaylistName, setSelectedPlaylistName] = React.useState(null);
    const [playlistDropdownOpen, setPlaylistDropdownOpen] = React.useState(false);
    const [playlistList, setPlaylistList] = React.useState([]);
    const [playlistsLoading, setPlaylistsLoading] = React.useState(false);
    const [playlistSearch, setPlaylistSearch] = React.useState('');
    const [playlistDropdownPos, setPlaylistDropdownPos] = React.useState({ top: 0, left: 0, width: 250 });
    const playlistDropdownRef = React.useRef(null);
    const playlistBtnRef = React.useRef(null);
    // Flip the "Open ▸ Playlist" picker above its button when there's no room below.
    React.useLayoutEffect(() => {
        if (!playlistDropdownOpen || !playlistDropdownRef.current || !playlistBtnRef.current) return;
        const rect = playlistBtnRef.current.getBoundingClientRect();
        const { top } = positionMenuByTrigger(playlistDropdownRef.current, rect, { gap: 2, align: null });
        setPlaylistDropdownPos(prev => (prev.top === top ? prev : { ...prev, top }));
    }, [playlistDropdownOpen, playlistList, playlistSearch, playlistsLoading]);
    const playlistSearchRef = React.useRef(null);

    // Selectors
    const {
        organizerFiles,
        organizerIsProcessing,
        organizerProgress,
        cancelRequested,
        autoAnalyze
    } = useSelector((state) => state.organizer);

    const logs = useSelector((state) => state.organizerLogs.logs);
    // DESIGN V2 — structural changes (tool header, group-by chips, panel
    // titles) are gated on this; the old structure is untouched while off.
    const v2 = useDesignV2();
    const v2AutoArmedRef = React.useRef(null);  // DESIGN V2: one auto-analyze per loaded source
    // Signed-in users' playlists live in the cloud — the picker lists those
    // and resolves their tracks to local files (utils/cloudPlaylistTracks).
    const accessToken = useSelector((state) => state.auth.accessToken);
    const useServer = !!accessToken;

    // Real covers for the play buttons. Keyed on isProcessing so the index
    // reloads when a scan finishes — the scan itself writes covers to the DB.
    const enrichedMap = useEnrichedTracks(organizerIsProcessing);

    const {
        operation,
        mainOrganization,
        secondaryOrganization,
        tertiaryOrganization,
        includeMonth,
        dateFormat,
        appendToBeginning
    } = useSelector((state) => state.organizerSettings);

    const {
        enableRecursive,
        concurrencyLevel,
    } = useSelector((state) => state.settings);

    // Any change to the source value bumps the epoch — including dispatches
    // from OUTSIDE this tab (playlist sidebar "Open in tool"), which the
    // per-handler bumps can't see.
    const sourceFiles_watch = React.useRef(organizerFiles);
    React.useEffect(() => {
        if (sourceFiles_watch.current !== organizerFiles) {
            sourceFiles_watch.current = organizerFiles;
            sourceEpochRef.current += 1;
        }
    }, [organizerFiles]);

    // Auto-analyze when triggered from playlist sidebar
    React.useEffect(() => {
        // DESIGN V2: a loaded source previews on its own; the button is the write step.
        // Arm once per load: a new files array, or the same array loaded again
        // after its results were cleared (not during a run, which clears too).
        if (v2 && organizerFiles.length > 0 && logs.length === 0 && !organizerIsProcessing && !autoAnalyze
            && (v2AutoArmedRef.current?.files !== organizerFiles || Date.now() - v2AutoArmedRef.current.at > 3000)) {
            v2AutoArmedRef.current = { files: organizerFiles, at: Date.now() };
            dispatch(setOrganizerAutoAnalyze(true));
            return;
        }
        if (autoAnalyze && organizerFiles.length > 0 && !organizerIsProcessing) {
            dispatch(setOrganizerAutoAnalyze(false));
            handleRunOrganizer(false);
        }
    }, [autoAnalyze, organizerFiles, logs.length, organizerIsProcessing]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close playlist dropdown on outside click
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (playlistDropdownRef.current && !playlistDropdownRef.current.contains(e.target)) {
                setPlaylistDropdownOpen(false);
            }
        };
        if (playlistDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [playlistDropdownOpen]);

    const handlePlaylistDropdownToggle = async (anchorEl) => {
        if (playlistDropdownOpen) { setPlaylistDropdownOpen(false); return; }
        setPlaylistSearch('');
        if (anchorEl) {
            const rect = anchorEl.getBoundingClientRect();
            setPlaylistDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 250) });
        }
        setPlaylistDropdownOpen(true);
        setTimeout(() => playlistSearchRef.current?.focus(), 50);
        setPlaylistsLoading(true);
        try {
            if (useServer) {
                const result = await refreshAndRetry((token) => window.electron.library.getPlaylists(token));
                setPlaylistList(result?.data || []);
            } else {
                const allPlaylists = await window.electron.db.playlists.getAll(false);
                setPlaylistList(allPlaylists || []);
            }
        } catch (error) {
            console.error('Error loading playlists:', error);
            toast.error('Failed to load playlists');
        } finally {
            setPlaylistsLoading(false);
        }
    };

    // Source identity guard (same as FixMyTracksTab): playlist selection
    // resolves ASYNCHRONOUSLY, and a run click racing it would act on the
    // PREVIOUS source — especially dangerous here, where Execute MOVES files.
    const sourceEpochRef = React.useRef(0);
    const [sourceResolving, setSourceResolving] = React.useState(false);

    const handlePlaylistSelect = async (playlist) => {
        setPlaylistDropdownOpen(false);
        setSourceResolving(true);
        try {
            let filePaths = [];
            let total = 0;
            if (useServer) {
                // Cloud playlist — resolve rows to local files (exact link /
                // origin path, one-time heuristic backfill for legacy rows).
                const loaded = await loadCloudPlaylistFiles(accessToken, playlist.id);
                if (!loaded || loaded.total === 0) {
                    toast.warning('This playlist has no tracks'); return;
                }
                total = loaded.total;
                filePaths = loaded.filePaths;
                if (filePaths.length === 0) {
                    toast.warning('None of this playlist’s tracks are on this device — these tools work on local audio files');
                    return;
                }
            } else {
                const playlistData = await window.electron.db.playlists.get(playlist.id, true);
                if (!playlistData || !playlistData.tracks || playlistData.tracks.length === 0) {
                    toast.warning('This playlist has no tracks'); return;
                }
                const tracks = playlistData.tracks.filter(t => t.file || t.filePath);
                if (tracks.length === 0) { toast.warning('No valid file paths found in playlist'); return; }
                // Filter out cloud/online tracks — they don't have local files
                total = tracks.length;
                filePaths = tracks
                    .map(t => t.file || t.filePath)
                    .filter(f => !f.startsWith('online://') && !f.startsWith('http://') && !f.startsWith('https://'));
                if (filePaths.length === 0) {
                    toast.warning('This playlist only contains cloud tracks — no local files to organize');
                    return;
                }
            }
            setSelectedPlaylistName(playlist.name);
            sourceEpochRef.current += 1;
            dispatch(setOrganizerFiles(filePaths));
            const skipped = total - filePaths.length;
            if (skipped > 0) toast.info(`Skipped ${skipped} ${skipped === 1 ? 'track' : 'tracks'} with no local file on this device`);
            track('playlist_opened_in_tool', {
                tab: 'organize-tracks',
                source: useServer ? 'cloud' : 'local',
                track_count: filePaths.length,
                skipped_count: skipped,
            });
        } catch (error) {
            console.error('Error loading playlist tracks:', error);
            toast.error('Failed to load playlist tracks');
        } finally {
            setSourceResolving(false);
        }
    };

    // Handlers
    const handleChooseDir = async () => {
        const chosenDir = await window.electron.openDir();
        if (!chosenDir || chosenDir.length === 0) return; // dialog canceled — keep current selection
        setSelectedPlaylistName(null);
        sourceEpochRef.current += 1;
        dispatch(setOrganizerFiles(chosenDir));
    };

    const handleChooseLibrary = async () => {
        try {
            const allTracks = await window.electron.db.tracks.getAll();
            const filePaths = (allTracks || [])
                .map(t => t.file_path || t.filePath || t.file)
                .filter(f => f && !f.startsWith('online://') && !f.startsWith('http://') && !f.startsWith('https://'));
            if (filePaths.length === 0) {
                toast.warning('Your library is empty'); return;
            }
            setSelectedPlaylistName('My Library');
            sourceEpochRef.current += 1;
            dispatch(setOrganizerFiles(filePaths));
        } catch (error) {
            console.error('Error loading library:', error);
            toast.error('Failed to load library');
        }
    };

    const handleResetFilePath = () => {
        sourceEpochRef.current += 1;
        dispatch(resetOrganizerFilePath());
        dispatch(clearLogs());
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFiles = Array.from(e.dataTransfer.files).map(file => window.electron.getPathForFile(file)).filter(Boolean);
        if (droppedFiles.length > 0) {
            sourceEpochRef.current += 1;
            dispatch(setOrganizerFiles(droppedFiles));
        }
    };

    const handleRefresh = () => {
        handleRunOrganizer(false); // Run analyze again
    };

    const handleStopProcessing = () => {
        dispatch(setOrganizerCancelRequested(true));
        dispatch(setOrganizerIsProcessing(false));
        window.electron.cancelOrganizing();
    };

    const handleRunOrganizer = async (execute) => {
        // Source captured at click time; abort if it changes during any await
        // below (see sourceEpochRef).
        const runEpoch = sourceEpochRef.current;
        const sourceChanged = () => sourceEpochRef.current !== runEpoch;
        // Time-based large-selection gate (silent for small sets) — applies to
        // library, playlist, folder and dropped files alike.
        const fileCount = await countAudioFiles(organizerFiles);
        const sourceLabel = selectedPlaylistName === 'My Library' ? 'library'
            : selectedPlaylistName ? 'playlist'
            : (organizerFiles.length === 1 && fileCount !== 1) ? 'folder' : 'selection';
        if (!(await confirmLargeSet(toast, fileCount, {
            secondsPerFile: SET_COST.organize,
            parallelism: effectiveParallelism(),
            sourceLabel,
            tab: 'organize_tracks',
        }))) return;
        if (sourceChanged()) return;

        window.electron.analytics.track('organize_run', { mode: execute ? 'execute' : 'preview', operation });
        dispatch(clearLogs()); // Clear old logs before running
        dispatch(setOrganizerIsProcessing(true));
        dispatch(setOrganizerProgress(0));

        const startTime = Date.now(); // Record the start time

        const updateProgressFromBackend = async ({progressPercentage, logs, notice}) => {
            // Follow-up DJ-library sync may report an app was open (e.g. "Rekordbox
            // is open — MixGoat will update it once it closes").
            if (notice) toast.info(notice);
            if (progressPercentage >= 100) {
                await waitRemaining(startTime); // Keep progress visible briefly so fast runs don't flash-complete (see minVisibleDelay)
                dispatch(setOrganizerProgress(progressPercentage));
                dispatch(setOrganizerIsProcessing(false));
                track('organize_run_complete', { success: true, track_count: organizerFiles.length, duration_ms: Date.now() - startTime });
            } else {
                dispatch(setOrganizerProgress(progressPercentage));
            }

            if (logs && logs.length > 0) {
                dispatch(updateOrAddLogs(logs));
            }
        };

        // Check if the single item is likely a directory (no file extension) or a file
        const isSingleDirectory = organizerFiles.length === 1 && !organizerFiles[0].match(/\.\w{2,5}$/);

        // Prepare options based on operation
        const options = {
            dir: isSingleDirectory ? organizerFiles[0] : null,
            files: isSingleDirectory ? null : organizerFiles,
            mainOrganization,
            secondaryOrganization: secondaryOrganization === 'none' ? null : secondaryOrganization,
            tertiaryOrganization: tertiaryOrganization === 'none' ? null : tertiaryOrganization,
            includeSubDirs: isSingleDirectory, // Only scan subdirectories when a directory is selected
            execute,
            // Mixpedia lookups fill styles/genres for style-based organization —
            // without the token the enricher runs offline and every track lands
            // in "Uncategorized".
            djpalToken: localStorage.getItem('djpal:token') || ''
        };

        if (operation === 'organize') {
            options.includeMonth = includeMonth;
            // Per-file ticks are folded into a few applies per second (see
            // utils/progressBatcher.js); the final tick flushes then applies.
            window.electron.organizeTracks(
                options,
                batchProgressTicks(updateProgressFromBackend)
            );
        } else {
            // appendNames
            options.dateFormat = dateFormat;
            options.appendToBeginning = appendToBeginning;
            window.electron.appendFileNames(
                options,
                batchProgressTicks(updateProgressFromBackend)
            );
        }
    };

    const handlePlayClick = async (filePath, e) => {
        e.stopPropagation();

        if (!filePath) {
            return;
        }

        try {
            // If clicking on currently playing track that's paused, resume it
            if (currentlyPlayingPath === filePath && !isPlaying) {
                if (window.musicPlayerInstance) {
                    window.musicPlayerInstance.play();
                }
                // Re-assert the list context (see FixMyTracksTab): resuming
                // must restore play-through-the-list, not solo play.
                setLocalListContext(dispatch, rowKeys, filePath, enrichedMap);
                setIsPlaying(true);
                dispatch(setAutoPlay(true));
                return;
            }

            // Prefer enriched (Mixpedia/ID3) title/artist/cover over the filename.
            const { title: trackName, artist, cover: enrichedCover } = getEnrichedParts(enrichedMap, filePath);

            // Load and play new track
            const musicSrc = await window.electron.getAudioUrl(filePath);

            // Fall back to embedded album art only when we have no enriched cover.
            let albumArtUrl = '';
            try {
                albumArtUrl = await window.electron.getAlbumArtUrl(filePath);
            } catch (err) {
            }

            const newTrack = {
                name: trackName,
                singer: artist,
                cover: enrichedCover || albumArtUrl,
                musicSrc
            };

            // Clear playlist and play new track
            dispatch(setAudioList([newTrack]));
            // Register the visible tree's files (depth-first display order) as
            // the player's list context so playback continues down the list
            // when this track ends — auto-radio only takes over after the
            // LAST row (same behavior as the Library tab).
            setLocalListContext(dispatch, rowKeys, filePath, enrichedMap);
            dispatch(setAutoPlay(true));
            setCurrentlyPlayingPath(filePath);
            setIsPlaying(true);
        } catch (error) {
            console.error('Error playing track:', error);
        }
    };

    const handlePauseClick = (e) => {
        e.stopPropagation();
        if (window.musicPlayerInstance) {
            window.musicPlayerInstance.pause();
        }
        setIsPlaying(false);
        dispatch(setAutoPlay(false));
    };

    const handleStopClick = (e) => {
        e.stopPropagation();
        if (window.musicPlayerInstance) {
            window.musicPlayerInstance.pause();
            window.musicPlayerInstance.currentTime = 0;
        }
        setIsPlaying(false);
        setCurrentlyPlayingPath(null);
        dispatch(setAutoPlay(false));
        dispatch(resetAudioList());
    };

    const handleAddToQueue = async (filePath, e) => {
        e.stopPropagation();

        if (!filePath) {
            return;
        }

        try {
            const musicSrc = await window.electron.getAudioUrl(filePath);

            let albumArtUrl = '';
            try {
                albumArtUrl = await window.electron.getAlbumArtUrl(filePath);
            } catch (err) {
            }

            // Prefer enriched (Mixpedia/ID3) title/artist/cover over the filename.
            const { title: trackName, artist, cover: enrichedCover } = getEnrichedParts(enrichedMap, filePath);

            const newTrack = {
                name: trackName,
                singer: artist,
                cover: enrichedCover || albumArtUrl,
                musicSrc
            };

            dispatch(addTrack(newTrack));
        } catch (error) {
            console.error('Error adding track to queue:', error);
        }
    };

    // Latest handlers for the memoized file rows (see OrganizeFileRow). The
    // row's `api` prop never changes identity; each method reads the current
    // closure through this ref, so rows don't re-render on every tab render.
    const rowHandlersRef = React.useRef({});
    const rowApi = React.useMemo(() => ({
        rowClick: (filePath, e) => rowHandlersRef.current.rowClick(filePath, e),
        rowContextMenu: (filePath, e, pane) => rowHandlersRef.current.rowContextMenu(filePath, e, pane),
        playClick: (filePath, e) => rowHandlersRef.current.playClick(filePath, e),
        pauseClick: (e) => rowHandlersRef.current.pauseClick(e),
        addToQueue: (filePath, e) => rowHandlersRef.current.addToQueue(filePath, e),
        removeFromList: (paths, via) => rowHandlersRef.current.removeFromList(paths, via),
    }), []);

    // Base directory for the tree views. organizerFiles[0] is a FILE for
    // playlist/library/picked sources — using it verbatim made the first
    // track vanish (its relative path collapsed to '') and nested everything
    // else under absolute-path segments.
    const treeBaseDir = React.useMemo(() => {
        const first = organizerFiles[0] || '';
        if (!first) return '';
        if (!/\.[a-z0-9]{2,5}$/i.test(first)) return first; // a directory
        const norm = first.replace(/\\/g, '/');
        return norm.slice(0, norm.lastIndexOf('/'));
    }, [organizerFiles]);

    // Function to build current structure tree (files as they are now)
    const buildCurrentStructure = (logs) => {
        const tree = {};

        logs.forEach(log => {
            if (!log.filePath) return;

            // After execution, the current structure should show the new filename/path
            // because that's what the files are actually called now
            let currentPath;
            if (log.status === 'renamed' || log.status === 'organized') {
                // File has been renamed/moved, so current state is the new path
                currentPath = log.newPath || log.targetPath || log.filePath;
            } else {
                // In analyze mode, current state is still the old path
                currentPath = log.filePath;
            }

            // Extract relative path from the base directory (may be gone if the
            // selection was cleared while stale logs are still rendered)
            const baseDir = treeBaseDir;
            // Normalize slashes for comparison
            const normCurrent = currentPath.replace(/\\/g, '/');
            const normBase = baseDir.replace(/\\/g, '/');
            let relativePath = normCurrent;
            if (normCurrent.startsWith(normBase)) {
                relativePath = normCurrent.substring(normBase.length);
            }
            // Remove leading slash
            if (relativePath.startsWith('/')) {
                relativePath = relativePath.substring(1);
            }

            const parts = relativePath.split('/').filter(p => p); // Remove empty parts

            let currentLevel = tree;
            parts.forEach((part, index) => {
                if (!currentLevel[part]) {
                    currentLevel[part] = {
                        name: part,
                        isFile: index === parts.length - 1,
                        children: {},
                        log: index === parts.length - 1 ? {...log, filePath: currentPath} : null,
                        path: parts.slice(0, index + 1).join('/')
                    };
                }
                currentLevel = currentLevel[part].children;
            });
        });

        return tree;
    };

    // Function to build expected tree structure from logs
    const buildExpectedStructure = (logs) => {
        const tree = {};

        logs.forEach(log => {
            if (!log.targetPath && !log.newPath) return;

            const fullPath = log.targetPath || log.newPath;

            // Extract relative path from the base directory (may be gone if the
            // selection was cleared while stale logs are still rendered)
            const baseDir = treeBaseDir;
            const normFull = fullPath.replace(/\\/g, '/');
            const normBase = baseDir.replace(/\\/g, '/');
            let relativePath = normFull;
            if (normFull.startsWith(normBase)) {
                relativePath = normFull.substring(normBase.length);
            }
            if (relativePath.startsWith('/')) {
                relativePath = relativePath.substring(1);
            }

            const parts = relativePath.split('/').filter(p => p); // Remove empty parts

            let currentLevel = tree;
            parts.forEach((part, index) => {
                if (!currentLevel[part]) {
                    currentLevel[part] = {
                        name: part,
                        isFile: index === parts.length - 1,
                        children: {},
                        log: index === parts.length - 1 ? log : null,
                        path: parts.slice(0, index + 1).join('/')
                    };
                }
                currentLevel = currentLevel[part].children;
            });
        });

        return tree;
    };

    // Display order for sibling tree nodes: folders before files, each
    // alphabetically — except the "Uncategorized" fallback folder, which is
    // pinned to the end so real genres/dates/labels read first.
    const sortedChildren = (childrenMap) => Object.values(childrenMap).sort((a, b) => {
        if (a.isFile !== b.isFile) return a.isFile ? 1 : -1;
        const aUncat = a.name === 'Uncategorized';
        const bUncat = b.name === 'Uncategorized';
        if (aUncat !== bUncat) return aUncat ? 1 : -1;
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
    });

    // Recursive function to render tree nodes. `pane` ('current'|'expected')
    // scopes the right-click menu key; row selection lives on the current pane.
    const renderTreeNode = (node, depth = 0, pane = 'current') => {
        const indent = depth * 20; // 20px per level
        const hasChildren = Object.keys(node.children).length > 0;
        const filePath = node.log?.filePath || node.log?.targetPath || node.log?.newPath;
        const selectable = pane === 'current' && node.isFile && !!filePath;
        const isSelected = selectable && selectedPaths.has(filePath);

        if (node.isFile) {
            const isCurrent = !!filePath && currentlyPlayingPath === filePath;
            return (
                <div key={node.path || node.name} className="tree-node-wrapper">
                    <OrganizeFileRow
                        filePath={filePath}
                        name={node.name}
                        indent={indent}
                        pane={pane}
                        selectable={selectable}
                        selected={isSelected}
                        playing={isCurrent && isPlaying}
                        ctxAt={rowCtx && rowCtx.key === `${pane}:${filePath}` ? rowCtx : null}
                        enrichedMap={enrichedMap}
                        api={rowApi}
                    />
                </div>
            );
        }

        return (
            <div key={node.path || node.name} className="tree-node-wrapper">
                <div
                    className="tree-node"
                    style={{ marginLeft: `${indent}px` }}
                >
                    <DirectoryIcon className="tree-icon folder-icon" />
                    <span className="tree-node-text">{node.name}</span>
                </div>
                {hasChildren && (
                    <div className="tree-children">
                        {sortedChildren(node.children).map(child => renderTreeNode(child, depth + 1, pane))}
                    </div>
                )}
            </div>
        );
    };

    // Cmd+F find is highlight-only — nothing narrows the preview trees.
    const filteredLogs = logs;

    // Memoized: row selection derives its ordered key list from the current
    // tree, so the tree identity must only change when its inputs do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const currentStructure = React.useMemo(() => buildCurrentStructure(filteredLogs), [filteredLogs, organizerFiles]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const expectedStructure = React.useMemo(() => buildExpectedStructure(filteredLogs), [filteredLogs, organizerFiles]);

    // ---- Multi-row selection (Finder semantics, shared hook) ----
    // Selection lives on the "Current Structure" pane (those are the actual
    // files); the Expected pane still gets the same right-click menu per row.
    // Keys = file paths in visible tree order (same sortedChildren walk the
    // renderer uses).
    const rowKeys = React.useMemo(() => {
        const seen = new Set();
        const keys = [];
        const walk = (childrenMap) => {
            for (const node of sortedChildren(childrenMap)) {
                if (node.isFile) {
                    const fp = node.log?.filePath || node.log?.targetPath || node.log?.newPath;
                    if (fp && !seen.has(fp)) { seen.add(fp); keys.push(fp); }
                }
                walk(node.children);
            }
        };
        walk(currentStructure);
        return keys;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStructure]);
    const keyIndex = React.useMemo(() => new Map(rowKeys.map((k, i) => [k, i])), [rowKeys]);

    // Remove rows from the SESSION list only (organizer log entries) —
    // nothing is deleted from disk or the local DB, so no undo toast.
    const removeFilesFromList = React.useCallback((paths, via) => {
        if (!paths?.length) return;
        paths.forEach((p) => dispatch(removeLogsForFile(p)));
        track('tracks_removed_from_list', { tab: 'organize_tracks', count: paths.length, via });
    }, [dispatch]);

    const listContainerRef = React.useRef(null);
    const scrollRowIntoView = React.useCallback((index, key) => {
        listContainerRef.current
            ?.querySelector(`[data-rowkey="${CSS.escape(key)}"]`)
            ?.scrollIntoView({ block: 'nearest' });
    }, []);
    const onDeleteSelected = React.useCallback((paths) => removeFilesFromList(paths, 'delete_key'), [removeFilesFromList]);
    const {
        selected: selectedPaths,
        orderedSelected,
        handleRowClick,
        selectOnly,
    } = useRowSelection({ keys: rowKeys, onDelete: onDeleteSelected, onArrowFocus: scrollRowIntoView, rowSelector: '.tree-node' });

    // Right-click on a file row. Current pane: Finder semantics (bulk menu
    // inside a multi-selection, else retarget + shared row menu). Expected
    // pane: shared row menu only — selection stays on the current pane.
    const handleRowContextMenu = (filePath, e, pane) => {
        if (!filePath) return;
        e.preventDefault();
        if (pane === 'current') {
            if (selectedPaths.size > 1 && selectedPaths.has(filePath)) {
                setBulkCtx({ x: e.clientX, y: e.clientY });
                return;
            }
            selectOnly(filePath, keyIndex.get(filePath));
        }
        setRowCtx({ key: `${pane}:${filePath}`, x: e.clientX, y: e.clientY, nonce: ++ctxNonceRef.current });
    };

    rowHandlersRef.current = {
        rowClick: (filePath, e) => handleRowClick(keyIndex.get(filePath), filePath, e),
        rowContextMenu: handleRowContextMenu,
        playClick: handlePlayClick,
        pauseClick: handlePauseClick,
        addToQueue: handleAddToQueue,
        removeFromList: removeFilesFromList,
    };

    // Shared by both designs: the Open menu (same #open-dropdown and
    // .dropdown-item hooks the QA matrix drives) and the playlist picker.
    const sourceLabel = organizerFiles.length === 0
        ? 'Open'
        : selectedPlaylistName
            ? selectedPlaylistName
            : organizerFiles.length > 1
                // Library / playlist loads hand over FILE paths; the folder picker hands over folders.
                ? `${organizerFiles.length} ${organizerFiles.every((f) => /\.[a-z0-9]{2,4}$/i.test(f)) ? 'files' : 'folders'}`
                : (organizerFiles[0].split('/').pop() || organizerFiles[0]);
    const openMenu = (
        <Dropdown>
            <Dropdown.Toggle
                className={v2 ? 'v2-btn v2-source-chip' : 'button choose-button'}
                id="open-dropdown"
                ref={playlistBtnRef}
                title={v2 ? (organizerFiles.length === 1 ? organizerFiles[0] : '') : undefined}
            >
                {v2 ? (
                    <>
                        <DirectoryIcon className="icon" />
                        <span className="v2-source-label">{sourceLabel}</span>
                        {selectedPlaylistName && organizerFiles.length > 0 && <span className="v2-faint v2-num">· {organizerFiles.length}</span>}
                    </>
                ) : (
                    <><OpenIcon className="icon" />Open</>
                )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item onClick={handleChooseDir}>Folder</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => handlePlaylistDropdownToggle(playlistBtnRef.current)}>Playlist</Dropdown.Item>
                <Dropdown.Item onClick={handleChooseLibrary}>My Library</Dropdown.Item>
                {v2 && organizerFiles.length > 0 && (
                    <>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => handleResetFilePath()}>Clear</Dropdown.Item>
                    </>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
    const playlistPicker = playlistDropdownOpen && (
        <div ref={playlistDropdownRef} className="smart-recs-dropdown-menu" style={{ position: 'fixed', top: playlistDropdownPos.top, left: playlistDropdownPos.left, width: playlistDropdownPos.width, zIndex: 1000 }}>
            <div className="dropdown-search-wrapper">
                <svg className="dropdown-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input ref={playlistSearchRef} className="dropdown-search-input" type="text" placeholder="Search playlists..." value={playlistSearch} onChange={(e) => setPlaylistSearch(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
            </div>
            <div className="dropdown-options">
                {playlistsLoading ? (
                    <div className="dropdown-skel" aria-label="Loading playlists">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="dropdown-skel-option"><span /></div>)}</div>
                ) : (
                    <>
                        {playlistList.filter(pl => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase())).map((pl) => (
                            <button key={pl.id} className="dropdown-option" onClick={() => handlePlaylistSelect(pl)}>
                                <span className="dropdown-option-name">{pl.name}</span>
                            </button>
                        ))}
                        {playlistList.filter(pl => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase())).length === 0 && (
                            playlistList.length === 0 ? (
                            <div className="dropdown-no-results" style={{ whiteSpace: 'normal', lineHeight: 1.4, textAlign: 'left', padding: '10px 12px' }}>
                                No playlists yet. These tools work on audio files on this computer — import a library (Rekordbox, Serato, Traktor, VirtualDJ) or add local tracks.
                            </div>
                        ) : (
                            <div className="dropdown-no-results">No playlists match your search</div>
                        )
                        )}
                    </>
                )}
            </div>
        </div>
    );

    // ---- DESIGN V2 header: "Group by: Genre › + then by" chips (same state
    // and conflict rules as OrganizerSettings), settings popover, actions.
    const GROUP_OPTIONS = [['style', 'Genre'], ['releaseDate', 'Release date'], ['label', 'Label']];
    const groupLabel = (v) => (GROUP_OPTIONS.find(([k]) => k === v) || [])[1] || 'None';
    const changeMain = (v) => {
        if (v === secondaryOrganization) dispatch(setSecondaryOrganization('none'));
        if (v === tertiaryOrganization) dispatch(setTertiaryOrganization('none'));
        dispatch(setMainOrganization(v));
    };
    const changeSecondary = (v) => {
        if (v === tertiaryOrganization && v !== 'none') dispatch(setTertiaryOrganization('none'));
        dispatch(setSecondaryOrganization(v));
        if (v === 'none') dispatch(setTertiaryOrganization('none'));
    };
    const groupChip = (id, value, onChange, taken, placeholder) => (
        <Dropdown key={id}>
            <Dropdown.Toggle className={`v2-chip sm v2-chip-dropdown${value && value !== 'none' ? '' : ' v2-chip-ghost'}`} id={id}>
                {value && value !== 'none' ? groupLabel(value) : placeholder}
            </Dropdown.Toggle>
            <Dropdown.Menu>
                {placeholder && value && value !== 'none' && <><Dropdown.Item onClick={() => onChange('none')}>None</Dropdown.Item><Dropdown.Divider /></>}
                {GROUP_OPTIONS.map(([k, l]) => (
                    <Dropdown.Item key={k} disabled={taken.includes(k)} active={value === k} onClick={() => onChange(k)}>{l}</Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
    const folderCount = Object.keys(expectedStructure).length;
    const processedCount = logs.filter((l) => l.filePath && (l.status === 'renamed' || l.status === 'organized')).length;
    const hasPreview = logs.length > 0;
    const runDisabled = organizerIsProcessing || sourceResolving || organizerFiles.length === 0;
    const v2Header = v2 && (
        <ToolHeader
            className="v2-tool-organize"
            source={<>{openMenu}{playlistPicker}</>}
            stats={organizerIsProcessing ? (
                <ProgressBarWithStop isProcessing={organizerIsProcessing} progress={organizerProgress} handleStopProcessing={handleStopProcessing} v2 />
            ) : organizerFiles.length === 0 ? (
                null /* the empty page below already says it; repeating it here read as a stray line */
            ) : hasPreview ? (
                <>
                    <b>{logs.length}</b> files{operation === 'organize' && <> → <b>{folderCount}</b> {folderCount === 1 ? 'folder' : 'folders'}</>}
                    {processedCount > 0 && <> · <b>{processedCount}</b> processed</>}
                </>
            ) : (
                <><b>{organizerFiles.length}</b> {organizerFiles.length === 1 && !selectedPlaylistName ? 'folder' : 'files'} · not previewed yet</>
            )}
            settings={(
                <>
                    <div className="v2-popover-title">Settings</div>
                    <div className="v2-popover-row"><div>Operation</div>
                        <div style={{ width: 180 }}><CustomSelect value={operation} onChange={(e) => dispatch(setOperation(e.target.value))} className="organizer-select compact-select"
                            options={[{ value: 'organize', label: 'Organize into folders' }, { value: 'appendNames', label: 'Append to filenames' }]} /></div></div>
                    {operation === 'organize' && (
                        <PopoverRow label="Include month in folders" hint="2024 › 08 instead of 2024"><ToggleSwitch id="v2-includeMonth" checked={!!includeMonth} onChange={(v) => dispatch(setIncludeMonth(v))} label="" /></PopoverRow>
                    )}
                    {operation === 'appendNames' && (
                        <>
                            <div className="v2-popover-row"><div>Date format</div>
                                <div style={{ width: 160 }}><CustomSelect value={dateFormat} onChange={(e) => dispatch(setDateFormat(e.target.value))} className="organizer-select compact-select"
                                    options={[{ value: 'year', label: 'Year only' }, { value: 'monthAndYear', label: 'Month and year' }, { value: 'fullDate', label: 'Full date' }]} /></div></div>
                            <PopoverRow label="Append at beginning"><ToggleSwitch id="v2-appendToBeginning" checked={!!appendToBeginning} onChange={(v) => dispatch(setAppendToBeginning(v))} label="" /></PopoverRow>
                        </>
                    )}
                </>
            )}
            actions={hasPreview ? (
                <>
                    <button type="button" className="v2-btn analyze-button" onClick={() => handleRunOrganizer(false)} disabled={runDisabled}>Preview again</button>
                    <button type="button" className="v2-btn primary" aria-disabled={runDisabled ? 'true' : undefined}
                        title={organizerFiles.length === 0 ? 'Open a folder or playlist first' : (operation === 'organize' ? 'Move the files into the folders on the right' : 'Rename the files as shown on the right')}
                        onClick={() => { if (runDisabled) return; if (!canAccess) { requireUpgrade(); return; } handleRunOrganizer(true); }}>
                        {operation === 'organize' ? `Organize ${logs.length} ${logs.length === 1 ? 'file' : 'files'}` : `Append names to ${logs.length} ${logs.length === 1 ? 'file' : 'files'}`}
                    </button>
                </>
            ) : organizerFiles.length > 0 && !organizerIsProcessing ? (
                <button type="button" className="v2-btn primary analyze-button" onClick={() => handleRunOrganizer(false)} disabled={runDisabled}
                    title={organizerFiles.length === 0 ? 'Open a folder or playlist first' : 'Preview the new structure — nothing is moved yet'}>Preview</button>
            ) : null}
        />
    );
    // The group-by chain sits in the row under the header, where the other tools
    // keep their filters: in the header it pushed Organize off a 1220px window.
    const v2GroupRow = v2 && (
        organizerFiles.length > 0 && (
            <div className="v2-filter-row">
                <div className="v2-groupby">
                    <span className="v2-faint">Group by</span>
                    {groupChip('v2-group-main', mainOrganization, changeMain, [], null)}
                    <span className="v2-faint">›</span>
                    {groupChip('v2-group-secondary', secondaryOrganization, changeSecondary, [mainOrganization], '+ then by')}
                    {secondaryOrganization && secondaryOrganization !== 'none' && (
                        <>
                            <span className="v2-faint">›</span>
                            {groupChip('v2-group-tertiary', tertiaryOrganization, (v) => dispatch(setTertiaryOrganization(v)), [mainOrganization, secondaryOrganization], '+ then by')}
                        </>
                    )}
                </div>
            </div>
        )
    );

    return (
        <div
            className={`tab-container ${v2 ? 'v2-tool-page v2-organize-page ' : ''}${isDragging ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {v2Header}
            {v2GroupRow}
            {!v2 && <div className="upper-part source-bar-header">
                <div className="source-bar">
                    <div className="source-bar-left">
                        {organizerIsProcessing ? (
                            <ProgressBarWithStop
                                isProcessing={organizerIsProcessing}
                                progress={organizerProgress}
                                handleStopProcessing={handleStopProcessing}
                            />
                        ) : (
                            <>
                                <DirectoryIcon className="source-bar-icon" />
                                <span className={`source-bar-title${organizerFiles.length === 0 ? ' empty' : ''}`} title={organizerFiles.length === 1 ? organizerFiles[0] : ''}>
                                    {organizerFiles.length === 0
                                        ? 'Click "Open" or drop a folder here'
                                        : selectedPlaylistName
                                            ? selectedPlaylistName
                                            : organizerFiles.length > 1
                                                ? `${organizerFiles.length} Folders`
                                                : (organizerFiles[0].split('/').pop() || organizerFiles[0])}
                                </span>
                                {selectedPlaylistName && organizerFiles.length > 0 && <span className="source-bar-meta">· {organizerFiles.length} files</span>}
                                {organizerFiles.length > 0 && (
                                    <button className="source-bar-clear" onClick={() => handleResetFilePath()} aria-label="Clear" title="Clear">&times;</button>
                                )}
                            </>
                        )}
                    </div>
                    <div className="source-bar-actions">
                        {logs.length > 0 && (
                            <button className="source-bar-icon-btn" onClick={() => handleRefresh()} title="Refresh" aria-label="Refresh"><RefreshIcon className="icon" /></button>
                        )}
                        {openMenu}
                        {playlistPicker}
                        <Button
                            className="button analyze-button main-button"
                            onClick={() => handleRunOrganizer(false)}
                            disabled={organizerIsProcessing || sourceResolving || organizerFiles.length === 0}
                        >
                            <AnalyzeFilesIcon className="icon"/>
                            Analyze
                        </Button>
                        <Button
                            className="button main-button"
                            onClick={() => { if (!canAccess) { requireUpgrade(); return; } handleRunOrganizer(true); }}
                            disabled={organizerIsProcessing || sourceResolving || organizerFiles.length === 0}
                        >
                            <EditFilenamesIcon className="icon"/>
                            {operation === 'organize' ? 'Organize' : 'Append Names'}
                            
                        </Button>
                    </div>
                </div>
            </div>}

            <div className="lower-part">
                <div className="log-container">
                    <div className="files-container comparison-view">
                        {organizerFiles.length === 0 ? (
                        <div className="ftx-empty-state" onClick={handleChooseDir} role="button" tabIndex={0}>
                            <DirectoryIcon className="ftx-empty-icon" />
                            <div className="ftx-empty-title">Drop a folder here to organize tracks</div>
                            <div className="ftx-empty-sub">or click to load a folder or a playlist</div>
                        </div>
                        ) : (<>
                        {!v2 && <div className="file-summary-container">
                            <div className="directory-container log-entry">
                                <DirectoryIcon className="icon"/>
                                <span>
                                    {selectedPlaylistName ? `Playlist: ${selectedPlaylistName} (${organizerFiles.length} tracks)` : `Directory: ${organizerFiles.length > 0 ? formatPath(organizerFiles[0]) : 'No directory selected'}`}
                                </span>
                            </div>
                            {/* Logs stream in during a run, so "Total Files" climbs; each count
                                sits in a slot as wide as the largest known total (StatCount +
                                utils/statDigits.js) so the items after it only re-flow at a digit
                                boundary — the organizer reports no total up front. */}
                            <div className="changes-container log-entry">
                                <span>
                                    <AudioFileIcon className='icon'/>
                                    Total Files: <StatCount value={logs.length || 0} digits={statDigits(logs.length, organizerFiles.length)} align="start" />
                                </span>
                                {logs.length > 0 && operation === 'organize' && (
                                    <span>
                                        <DirectoryIcon className='icon'/>
                                        Folders Created: <StatCount value={Object.keys(expectedStructure).length} digits={statDigits(logs.length, organizerFiles.length)} align="start" />
                                    </span>
                                )}
                                {logs.length > 0 && (
                                    <span>
                                        <AudioFileIcon className='icon'/>
                                        Files Processed: <StatCount value={filteredLogs.filter(log => log.filePath).length} digits={statDigits(logs.length, organizerFiles.length)} align="start" />
                                    </span>
                                )}
                            </div>
                        </div>}
                        <div className="comparison-container">
                            <div className="comparison-column">
                                {v2 ? (
                                    <div className="v2-section"><h3>Now</h3><small title={treeBaseDir}>{selectedPlaylistName || (treeBaseDir || '').split(/[\\/]/).filter(Boolean).pop()}</small></div>
                                ) : (
                                    <h3 className="comparison-title">Current Structure</h3>
                                )}
                                <div className="file-list-container tree-view-container" ref={listContainerRef}>
                                    {Object.keys(currentStructure).length > 0 ? (
                                        sortedChildren(currentStructure).map(node => renderTreeNode(node, 0, 'current'))
                                    ) : (
                                        <div className="no-files-message">
                                            <AudioFileIcon className="no-files-icon" />
                                            <span>No audio files were found in folder</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="comparison-divider">{v2 && <span className="v2-arrow" aria-hidden="true">→</span>}</div>
                            <div className="comparison-column">
                                {v2 ? (
                                    <div className="v2-section">
                                        <h3>{processedCount > 0 ? 'Organized' : (operation === 'organize' ? 'After organizing' : 'After renaming')}</h3>
                                        {hasPreview && operation === 'organize' && <small>· {folderCount} {folderCount === 1 ? 'folder' : 'folders'}</small>}
                                    </div>
                                ) : (
                                    <h3 className="comparison-title">Expected Structure</h3>
                                )}
                                <div className="file-list-container tree-view-container">
                                    {Object.keys(expectedStructure).length > 0 ? (
                                        sortedChildren(expectedStructure).map(node => renderTreeNode(node, 0, 'expected'))
                                    ) : (
                                        <div className="no-files-message">
                                            <AudioFileIcon className="no-files-icon" />
                                            <span>No files to display</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        </>)}
                    </div>
                    {!v2 && <OrganizerSettings/>}
                </div>
            </div>
            {bulkCtx && orderedSelected.length > 1 && (
                <BulkRowContextMenu
                    x={bulkCtx.x}
                    y={bulkCtx.y}
                    onClose={() => setBulkCtx(null)}
                    analyticsFrom="organize_tracks"
                    count={orderedSelected.length}
                    items={[
                        {
                            label: `Add ${orderedSelected.length} Tracks to Playlist`,
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/><rect x="3" y="3" width="18" height="18" rx="2"/></svg>,
                            onClick: () => setBulkPlaylistTargets(orderedSelected),
                        },
                        { divider: true },
                        {
                            label: `Remove ${orderedSelected.length} Tracks from list`,
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14H7L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>,
                            danger: true,
                            onClick: () => removeFilesFromList(orderedSelected, 'menu'),
                        },
                    ]}
                />
            )}
            {bulkPlaylistTargets.length > 0 && (
                <AddToPlaylistModal
                    show={true}
                    onHide={() => setBulkPlaylistTargets([])}
                    bulkFilePaths={bulkPlaylistTargets}
                    initialTab="add"
                    bulkLabel={`${bulkPlaylistTargets.length} tracks`}
                />
            )}
        </div>
    );
};

export default OrganizeTracksTab;
